import React, { useState, useMemo, useEffect } from "react";
import { useGetPublicLearningResourcesQuery } from "../../../services/api";

const TYPES = ["All", "Articles", "Videos", "Exercises", "Courses", "Workshops", "Other"];

export default function LearningLibraryModal({ isOpen, onClose, onSelect }) {
  const [search, setSearch] = useState("");
  const [selectedType, setSelectedType] = useState("All");
  const [addedResourceId, setAddedResourceId] = useState(null);

  const { data: resources = [], isLoading } = useGetPublicLearningResourcesQuery(
    { type: selectedType === "All" ? undefined : selectedType, search: search.trim() || undefined },
    { skip: !isOpen }
  );

  // Clear "Added!" feedback after 2 seconds
  useEffect(() => {
    if (addedResourceId) {
      const timer = setTimeout(() => setAddedResourceId(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [addedResourceId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setAddedResourceId(null);
    }
  }, [isOpen]);

  const handleSelect = (e, resource) => {
    e.stopPropagation();
    onSelect(resource);
    setAddedResourceId(resource._id);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="library-modal-overlay" onClick={handleOverlayClick}>
      <div className="library-modal">
        <div className="library-modal-header">
          <h2>Select from Learning Library</h2>
          <button className="library-modal-close" onClick={onClose} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="library-modal-filters">
          <div className="library-search-wrap">
            <svg className="library-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              type="text"
              className="library-search-input"
              placeholder="Search resources..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <select
            className="library-type-select"
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
          >
            {TYPES.map((type) => (
              <option key={type} value={type}>
                {type === "All" ? "All Types" : type}
              </option>
            ))}
          </select>
        </div>

        <div className="library-modal-content">
          {isLoading ? (
            <div className="library-loading">Loading resources...</div>
          ) : resources.length === 0 ? (
            <div className="library-empty">No resources found.</div>
          ) : (
            <div className="library-resource-list">
              {resources.map((resource) => (
                <div
                  key={resource._id}
                  className={`library-resource-item ${addedResourceId === resource._id ? 'just-added' : ''}`}
                  onClick={(e) => handleSelect(e, resource)}
                >
                  <div className="library-resource-info">
                    <div className="library-resource-title">{resource.title}</div>
                    <div className="library-resource-meta">
                      <span className="library-resource-type">{resource.type}</span>
                      {resource.description && (
                        <span className="library-resource-desc">
                          {resource.description.substring(0, 100)}
                          {resource.description.length > 100 ? "..." : ""}
                        </span>
                      )}
                    </div>
                  </div>
                  {addedResourceId === resource._id ? (
                    <span className="library-added-badge">Added!</span>
                  ) : (
                    <button type="button" className="library-select-btn">Select</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
